import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface ITesting {
  id: string;
  sessionName: string;
  senderId: string;
  destinationNumber: string;
  vendorConnectionProfileId: string;
  executedDate?: Date;
  createdDate?: Date;
  createdBy?: string;
  status?: string;

  messages: IMessage[];
}

export interface ITestingHistory {
  id: string;
  snapshot: ITesting & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  status: string;
  createdBy: string;
}

export interface IMessage {
  id?: number;
  senderId?: string;
  destinationNumber?: string;
  message: string;
  testingSessionId?: number;
  vendorConnectionProfileId?: string;
}

export const exportTesting = async (
  params: Params<ITesting> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/testing-sessions/csv", {
      params,
      responseType: "blob",
    });

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, "Testing.csv");

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching Testing:",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchTestings(
  params: Params<ITesting>
): Promise<IPageable<ITesting>> {
  try {
    const response = await axiosInstance.get("/testing-sessions", {
      params,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error("The requested resource was not found.");
    }
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching Testings."
    );
  }
}
export async function fetchTestingsById(
  id: string,
  params: Params<ITesting> = {}
): Promise<IPageable<ITesting>> {
  try {
    const response = await axiosInstance.get("/testing-sessions", {
      params: { ...params, id },
    });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while fetching Testings by id=${id}.`
    );
  }
}

export async function fetchTesting(id: ITesting["id"]): Promise<ITesting> {
  try {
    const response = await axiosInstance.get(`/testing-sessions/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting Testing/${id}.`
    );
  }
}

export const addTestingApi = async (data: Partial<ITesting>) => {
  try {
    const response = await axiosInstance.post(`/testing-sessions`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the Testing."
    );
  }
};

export async function editTestingSessions(
  data: Partial<ITesting>
): Promise<ITesting> {
  try {
    const response = await axiosInstance.put(
      `/testing-sessions/${data.id}`,
      data
    );
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while updating testing-sessions/${data.id}.`
    );
  }
}

export const getTestingById = async (id: string): Promise<ITesting> => {
  try {
    const response = await axiosInstance.get(`/testing-sessions/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message);
  }
};

export const useTestingByIdQuery = (
  id: string
): UseQueryResult<ITesting, Error> => {
  return useQuery({
    queryKey: ["testing", id],
    queryFn: () => getTestingById(id),
    enabled: !!id,
    refetchInterval: 1000,
  });
};

export const Testing = async (id: string): Promise<ITesting> => {
  try {
    const response = await axiosInstance.post(
      `/testing-sessions/execute/${id}`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message);
  }
};

export const useTestingQuery = (
  params: Params<ITesting>
): UseQueryResult<IPageable<ITesting>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["testing", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchTestings(effectiveParams),
  });
};
